import { useState, useRef, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import './TimePicker.css';

const hours = Array.from({ length: 24 }, (_, i) => String(i).padStart(2, '0'));
const minutes = Array.from({ length: 12 }, (_, i) => String(i * 5).padStart(2, '0'));

const POPUP_HEIGHT = 280;

const TimePicker = ({ value, onChange }) => {
  const [open, setOpen] = useState(null);
  const [pos, setPos] = useState({ top: 0, left: 0 });
  const ref = useRef(null);
  const startRef = useRef(null);
  const endRef = useRef(null);
  const scrollRefs = {
    startH: useRef(null), startM: useRef(null),
    endH: useRef(null), endM: useRef(null),
  };

  const strVal = typeof value === 'string' ? value : '';
  const parts = strVal.split(' - ');
  const st = parts[0] || '', et = parts[1] || '';
  const sH = st.split(':')[0] || '', sM = st.split(':')[1] || '';
  const eH = et.split(':')[0] || '', eM = et.split(':')[1] || '';

  const calcPos = useCallback((which) => {
    const btnEl = which === 'start' ? startRef.current : endRef.current;
    if (!btnEl) return;
    const rect = btnEl.getBoundingClientRect();
    const spaceBelow = window.innerHeight - rect.bottom;
    const openUp = spaceBelow < POPUP_HEIGHT && rect.top > spaceBelow;
    const popupW = 140;
    let left = rect.left;
    if (left + popupW > window.innerWidth) left = rect.right - popupW;
    if (left < 4) left = 4;
    setPos({
      top: openUp ? rect.top - POPUP_HEIGHT - 4 : rect.bottom + 4,
      left,
    });
  }, []);

  useEffect(() => {
    const handleClick = (e) => {
      if (ref.current && !ref.current.contains(e.target) && !e.target.closest('.tp-popup')) setOpen(null);
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  useEffect(() => {
    if (open) calcPos(open);
  }, [open, calcPos]);

  useEffect(() => {
    const key = open === 'start' ? 'start' : 'end';
    const h = open === 'start' ? sH : eH;
    const m = open === 'start' ? sM : eM;
    const hRef = scrollRefs[key + 'H'];
    const mRef = scrollRefs[key + 'M'];
    if (open) {
      setTimeout(() => {
        const hIdx = hours.indexOf(h);
        const mIdx = minutes.indexOf(m);
        if (hRef.current && hIdx > 0) hRef.current.scrollTop = hIdx * 36;
        if (mRef.current && mIdx > 0) mRef.current.scrollTop = mIdx * 36;
      }, 10);
    }
  }, [open, sH, sM, eH, eM]);

  const build = (newSH, newSM, newEH, newEM) => {
    const t1 = newSH && newSM ? `${newSH}:${newSM}` : '';
    const t2 = newEH && newEM ? `${newEH}:${newEM}` : '';
    onChange(`${t1} - ${t2}`);
  };

  const pickStart = (h, m) => {
    build(h ?? (sH || '09'), m ?? (sM || '00'), eH, eM);
    if (h !== undefined && m !== undefined) setOpen(null);
  };

  const pickEnd = (h, m) => {
    build(sH, sM, h ?? (eH || '18'), m ?? (eM || '00'));
    if (h !== undefined && m !== undefined) setOpen(null);
  };

  const display = (h, m) => h && m ? `${h}:${m}` : '--:--';

  const renderPopup = (which) => {
    const isStart = which === 'start';
    const curH = isStart ? sH : eH;
    const curM = isStart ? sM : eM;
    const pick = isStart ? pickStart : pickEnd;
    const hRef = scrollRefs[which + 'H'];
    const mRef = scrollRefs[which + 'M'];

    return createPortal(
      <div className="tp-popup" style={{ top: pos.top, left: pos.left }} onMouseDown={(e) => e.stopPropagation()}>
        <div className="tp-popup-title">{isStart ? 'დაწყება' : 'დასრულება'}</div>
        <div className="tp-cols">
          <div className="tp-col" ref={hRef}>
            {hours.map((h) => (
              <div key={h} className={`tp-item ${h === curH ? 'tp-active' : ''}`} onClick={() => pick(h, curM || undefined)}>{h}</div>
            ))}
          </div>
          <div className="tp-col-sep">:</div>
          <div className="tp-col" ref={mRef}>
            {minutes.map((m) => (
              <div key={m} className={`tp-item ${m === curM ? 'tp-active' : ''}`} onClick={() => pick(curH || undefined, m)}>{m}</div>
            ))}
          </div>
        </div>
      </div>,
      document.body
    );
  };

  return (
    <div className="tp-wrap" ref={ref}>
      <div className="tp-display" ref={startRef} onClick={() => setOpen(open === 'start' ? null : 'start')}>
        <span className={`tp-time ${sH && sM ? 'tp-filled' : ''}`}>{display(sH, sM)}</span>
      </div>
      <span className="tp-arrow">→</span>
      <div className="tp-display" ref={endRef} onClick={() => setOpen(open === 'end' ? null : 'end')}>
        <span className={`tp-time ${eH && eM ? 'tp-filled' : ''}`}>{display(eH, eM)}</span>
      </div>
      {open && renderPopup(open)}
    </div>
  );
};

export default TimePicker;
